import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router';
import { object, string, ref } from 'yup';
import useForm from '@jwp/ott-hooks-react/src/useForm';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { PATH_SIGN_IN, PATH_FORGOT_PASSWORD } from '@jwp/ott-common/src/paths';
import { useAuth } from '@jwp/ott-hooks-react/src/useAuth';
import Visibility from '@jwp/ott-theme/assets/icons/eye-on.svg?react';
import VisibilityOff from '@jwp/ott-theme/assets/icons/eye-off.svg?react';
import useToggle from '@jwp/ott-hooks-react/src/useToggle';
import { modalURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import { showToast } from '@jwp/ott-common/src/stores/ToastStore';

import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import Link from '../Link/Link';
import Icon from '../Icon/Icon';
import Button from '../Button/Button';
import styles from '../SignIn/SignIn.module.scss';

function BrandLogo({ appConfig, siteName }: Readonly<{ appConfig: any; siteName: string | undefined }>) {
  return (
    <div className={styles.brand}>
      {appConfig?.branding?.logo_url ? (
        <img src={appConfig.branding.logo_url} alt={siteName} className={styles.brandLogoImage} />
      ) : (
        <div className={styles.brandLogo} />
      )}
    </div>
  );
}

function SignInBackgroundVideo() {
  return (
    <div className={styles.videoLayer} aria-hidden>
      <video className={styles.videoBackground} autoPlay muted loop playsInline preload="metadata">
        <source src="/videos/signin-background.mp4" type="video/mp4" />
      </video>
    </div>
  );
}

const ResetPassword = () => {
  const { t } = useTranslation('account');
  const navigate = useNavigate();
  const location = useLocation();
  const { siteName, appConfig } = useConfigStore((s) => ({ siteName: s.config.siteName, appConfig: s.appConfig }));
  const { resetPassword, resetPasswordError, resetPasswordLoading } = useAuth();
  const [viewPassword, toggleViewPassword] = useToggle();
  const [viewConfirmPassword, toggleViewConfirmPassword] = useToggle();

  useEffect(() => {
    const email = sessionStorage.getItem("fp_email");
    const code = sessionStorage.getItem("fp_code");
    if (!email || !code) {
      navigate(PATH_FORGOT_PASSWORD);
    }
  }, [navigate]);

  const { values, errors, submitting, handleSubmit, handleChange } = useForm<{ password: string; confirmPassword: string }>({
    initialValues: { password: '', confirmPassword: '' },
    silentValidation: true,
    validationSchema: object().shape({
      password: string()
        .required(t('registration.field_required', { field: t('reset.password') }))
        .min(8, t('registration.password_helper_text'))
        .matches(/[A-Za-z]/, t('reset.password_one_letter', { defaultValue: 'Password must contain at least one letter' }))
        .matches(/\d/, t('reset.password_one_number', { defaultValue: 'Password must contain at least one number' }))
        .matches(/[%\-_!@#$*=&.]/, t('reset.password_special_character', { defaultValue: 'Password must contain at least one special character' })),
      confirmPassword: string()
        .oneOf([ref("password")], t('registration.confirm_password_mismatch'))
        .required(t('registration.field_required', { field: t('reset.confirm_password') })),
    }),
    onSubmit: async ({ password }) => {
      const email = sessionStorage.getItem("fp_email");
      const code = sessionStorage.getItem("fp_code");
      if (!email || !code) return;

      try {
        const response: any = await resetPassword(code, email, password);
        if (response?.errors && response.errors.length > 0) {
          throw new Error(response.errors[0].message);
        }
        if (response?.data) {
          sessionStorage.removeItem("fp_email");
          sessionStorage.removeItem("fp_code");
          navigate(modalURLFromLocation(location, 'reset-password-success-play'), { replace: true });
        }
      } catch (err: any) {
        console.error(err);
        throw err;
      }
    },
    onSubmitError: ({ error }) => {
      let errorMessage = t('login.unexpected_error');
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      showToast(errorMessage, 'error');
    },
  });

  return (
    <div className={styles.root}>
      <Helmet>
        <title>{t('reset.password_reset')}</title>
      </Helmet>
      <SignInBackgroundVideo />
      <BrandLogo appConfig={appConfig} siteName={siteName} />
      <main className={styles.main}>
        <div className={styles.overlayWrap}>
          {submitting || resetPasswordLoading ? (
            <div className={styles.loaderLayer} aria-live="polite" aria-busy>
              <LoadingOverlay transparentBackground inline />
            </div>
          ) : null}
          <div className={styles.card}>
            <h1 className={styles.cardTitle}>{t('reset.password_reset')}</h1>
            <p className={styles.cardSubtitle}>{t('reset.enter_new_password')}</p>

            <form onSubmit={handleSubmit} noValidate>

              <div className={styles.field}>
                <label htmlFor="reset-password" className={styles.fieldLabel}>
                  {t('reset.password')}
                  <span className={styles.required} aria-hidden>
                    *
                  </span>
                </label>
                <div className={`${styles.inputWrap} ${styles.inputWithToggle}`}>
                  <input
                    id="reset-password"
                    name="password"
                    type={viewPassword ? 'text' : 'password'}
                    className={styles.input}
                    value={values.password}
                    onChange={handleChange}
                    placeholder={t('reset.new_password_placeholder')}
                    required
                  />
                  <button
                    type="button"
                    className={styles.toggleVisibility}
                    onClick={() => toggleViewPassword()}
                    aria-label={t('login.view_password')}
                    aria-pressed={viewPassword}
                  >
                    <Icon icon={viewPassword ? Visibility : VisibilityOff} />
                  </button>
                </div>
                <p className={styles.helperError} role="alert" style={{ visibility: errors.password ? 'visible' : 'hidden' }}>
                  {errors.password || '\u00A0'}
                </p>
              </div>

              <div className={styles.field}>
                <label htmlFor="confirm-password" className={styles.fieldLabel}>
                  {t('reset.confirm_password')}
                  <span className={styles.required} aria-hidden>
                    *
                  </span>
                </label>
                <div className={`${styles.inputWrap} ${styles.inputWithToggle}`}>
                  <input
                    id="confirm-password"
                    name="confirmPassword"
                    type={viewConfirmPassword ? 'text' : 'password'}
                    className={styles.input}
                    value={values.confirmPassword}
                    onChange={handleChange}
                    placeholder={t('reset.confirm_password_placeholder')}
                    required
                  />
                  <button
                    type="button"
                    className={styles.toggleVisibility}
                    onClick={() => toggleViewConfirmPassword()}
                    aria-label={t('login.view_password')}
                    aria-pressed={viewConfirmPassword}
                  >
                    <Icon icon={viewConfirmPassword ? Visibility : VisibilityOff} />
                  </button>
                </div>
                <p className={styles.helperError} role="alert" style={{ visibility: errors.confirmPassword ? 'visible' : 'hidden' }}>
                  {errors.confirmPassword || '\u00A0'}
                </p>
              </div>
              <Button type="submit" color="primary" fullWidth size="large" disabled={submitting} label={t('reset.reset_password_button')} />
            </form>

            <p className={styles.footer}>
              {t('registration.already_account')}{' '}
              <Link className={styles.signUpLink} to={PATH_SIGN_IN}>
                {t('reset.sign_in')}
              </Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ResetPassword;
